import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

// MUI Components
import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TablePagination from '@mui/material/TablePagination';

// Components
import ErrorLogsModal from './error-logs-modal';
import SiteTableHeader from './site-table-header';
import Checkbox from '../form-fields/checkbox';
import EditSiteModal from './edit-site-modal';
import EditableEmail from './editable-email';

const ROWS_PER_PAGE = 15;

// `sort` is the key the API sorts on; columns without one are not sortable.
const COLUMNS = [
    { label: 'Domain', sort: 'domain' },
    { label: 'Stage' },
    { label: 'Email', sort: 'admin_email' },
    { label: 'Server' },
    { label: 'Cleanup', sort: 'error_logs_total_size' },
    { label: 'Last Crawl Status', sort: 'crawl_status' }
];

const SiteTable = () => {

    const [ loading, setLoading ]   = useState(true);
    const [ filters, setFilters ]   = useState({
        server: null,
        version: null,
        status: null
    });

    const [ search, setSearch ]     = useState('');
    const [ sites, setSites ]       = useState([]);
    const [ page, setPage ]         = useState(0);
    const [ sort, setSort ]         = useState({ key: null, direction: 'asc' });

    const [ showSitetest, setShowSitetest ] = useState(false);

    const [ selectedIds, setSelectedIds ]       = useState([]);
    const [ selectedSite, setSelectedSite ]     = useState(null);
    const [ editSite, setEditSite ]             = useState(null);

    const getSites = () => {
        setLoading(true);
        axios.get('/sites', {
            params: {
                filters: filters,
                search: search,
                show_sitetest: showSitetest,
                sort: sort.key,
                direction: sort.direction
            }
        }).then(response => {
            setSites(response.data.sites);
            // Keeps bulk actions from targeting sites that filters/search have removed.
            const availableIds = new Set(response.data.sites.map((site) => site.id));
            setSelectedIds((current) => current.filter((id) => availableIds.has(id)));
            setLoading(false);
        });
    }

    useEffect(() => {
        setPage(0);
        getSites();
    }, [filters, search, showSitetest, sort]);

    const toggleSort = (key) => setSort((current) => ({
        key,
        direction: (current.key === key && current.direction === 'asc') ? 'desc' : 'asc'
    }));

    const maxPage = Math.max(0, Math.ceil(sites.length / ROWS_PER_PAGE) - 1);
    useEffect(() => {
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [page, maxPage]);

    const pagedSites = sites.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allSelected = sites.length > 0 && sites.length === selectedIds.length;

    const toggleSelectAll = (checked) => {
        setSelectedIds(checked ? sites.map((site) => site.id) : []);
    };

    const toggleSite = (id, checked) => {
        setSelectedIds((current) => {
            if( ! checked ){
                return current.filter((selectedId) => selectedId !== id);
            }
            return current.includes(id) ? current : [ ...current, id ];
        });
    };

    const clearSelection = () => setSelectedIds([]);

    return (
        <Box className='site-table-container'>
            <SiteTableHeader
                search={search}
                setSearch={setSearch}
                filters={filters} 
                setFilters={setFilters} 
                getSites={getSites}
                selectedIds={selectedIds}
                clearSelection={clearSelection}
                showSitetest={showSitetest}
                setShowSitetest={setShowSitetest}
            />
            {(loading) ? 
                <Box className='site-table-container-loading'>
                    <CircularProgress />
                </Box>
            : (
                <>
                    {(sites.length > 0) ? (
                        <Box className='site-table-container'>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Checkbox
                                                    checked={allSelected}
                                                    onChange={toggleSelectAll}
                                                />
                                            </TableCell>
                                            {COLUMNS.map((column) => (
                                                <TableCell key={column.label} sortDirection={sort.key === column.sort ? sort.direction : false}>
                                                    {column.sort ? (
                                                        <TableSortLabel
                                                            active={sort.key === column.sort}
                                                            direction={sort.key === column.sort ? sort.direction : 'asc'}
                                                            onClick={() => toggleSort(column.sort)}
                                                        >
                                                            {column.label}
                                                        </TableSortLabel>
                                                    ) : column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedSites.map((site) => (
                                            <TableRow key={site.id}>
                                                <TableCell>
                                                    <Checkbox
                                                        checked={selectedIdSet.has(site.id)}
                                                        onChange={(checked) => toggleSite(site.id, checked)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Button className='no-uppercase site-table-domain-button' onClick={() => setEditSite(site)}>
                                                        {site.domain.length > 20 ? site.domain.substring(0, 20) + '...' : site.domain}
                                                    </Button>
                                                </TableCell>
                                                <TableCell>
                                                    {site.stage_sites ? site.stage_sites.map(s => (
                                                        <Button 
                                                            key={s.id} 
                                                            className='no-uppercase' 
                                                            onClick={() => setEditSite(s)}>
                                                                {s.domain}
                                                        </Button>
                                                    )) : ''}
                                                </TableCell>
                                                <TableCell>
                                                    <EditableEmail site={site} />
                                                </TableCell>
                                                <TableCell>{site.server.name}</TableCell>
                                                <TableCell>
                                                    <Button onClick={() => setSelectedSite(site)}>
                                                        {site.error_logs_total_size}
                                                    </Button>    
                                                </TableCell>
                                                <TableCell>{site.crawl_status}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component='div'
                                count={sites.length}
                                page={page}
                                onPageChange={(_event, nextPage) => setPage(nextPage)}
                                rowsPerPage={ROWS_PER_PAGE}
                                rowsPerPageOptions={[ROWS_PER_PAGE]}
                            />
                        </Box>
                    ) : (
                        <Box className='site-table-container-empty'>
                            <Typography variant='h6'>No sites found.</Typography>
                        </Box>
                    )}
                </>
            )}
            <ErrorLogsModal site={selectedSite} setSelectedSite={setSelectedSite} />
            <EditSiteModal site={editSite} setEditSite={(site, refresh) => {
                setEditSite(site);
                if( refresh ){
                    getSites();
                }
            }} />
        </Box>
    )

}

export default SiteTable;